import React, { useState, useEffect, useRef } from 'react';
import { getFurnitureTypes, getFurnitureModelsByTypeId, getModelColors, getModelSizes, getProductPrice, getProductVariantsByTypeId, logError } from '../../api';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { ProductEditorModal } from '../ProductEditor';
import '../Global/Style/OrderProductsForm.css';
import PriceConfirmation from '../Global/PriceConfirmation';
import ProductSelector from '../Global/ProductSelector';
import FavoriteDropdown from '../Global/FavoriteDropdown';
import { isPropietarioOrAdminOrGestion } from '../../services/AuthService';

const OrderProductsForm = ({ product, onPriceChange, onFormCompletion, furnitureTypes = [] }) => {
    const [isSpecialProduct, setIsSpecialProduct] = useState(false);
    const [specialProductData, setSpecialProductData] = useState({ description: '', price: '0', productTypeId: '' });
    const [models, setModels] = useState([]);
    const [colors, setColors] = useState([]);
    const [sizes, setSizes] = useState([]);
    const [selectedFurnitureType, setSelectedFurnitureType] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [selectedSize, setSelectedSize] = useState('');
    const [price, setPrice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isPriceManuallyEdited, setIsPriceManuallyEdited] = useState(false);
    const [dropdownHasChanged, setDropdownHasChanged] = useState(false);
    const [manualPrice, setManualPrice] = useState(null);
    const [quantity, setQuantity] = useState(1); // Agregamos el estado de cantidad
    const priceInputRef = useRef(null);
    const [basePrice, setBasePrice] = useState(null);
    const [skipAutoSelection, setSkipAutoSelection] = useState(false);
    const [variants, setVariants] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState([]);

    // Estado para manejar la visibilidad del modal
    const [showAddProductModal, setShowAddProductModal] = useState(false);

    const resetOptions = () => {
        setSelectedModel('');
        setModels([]);
        setSelectedColor('');
        setColors([]);
        setSelectedSize('');
        setSizes([]);
    };
    
    const autoSelectIfSingle = (data, setSelectedFn) => {
        if (data.length === 1) {
            setSelectedFn(data[0].id);
            return data[0].id;
        } else {
            setSelectedFn('');
            return '';
        }
    };
    
    useEffect(() => {
        if (!isPriceManuallyEdited) {
          setManualPrice(price);
        }
      }, [price]);

    useEffect(() => {
        if (selectedFurnitureType) {
            const fetchVariants = async () => {
                try {
                    const variantsData = await getProductVariantsByTypeId(selectedFurnitureType, false);
                    setVariants(variantsData);
                    setSelectedVariants([]);
                } catch (error) {
                    logError(error);
                    console.error('Error fetching variants:', error);
                }
            };
            fetchVariants();
        } else {
            setVariants([]);
            setSelectedVariants([]);
        }
    }, [selectedFurnitureType]);
    
    // Función para validar si todos los campos requeridos están completos
    const isFormComplete = () => {
        if (isSpecialProduct) {
            return false; // Para productos especiales, no usar isFormComplete
        }
        return (
            selectedFurnitureType !== '' &&
            selectedModel !== '' &&
            selectedColor !== '' &&
            selectedSize !== ''
        );
    };
    
    const isSpecialProductComplete = () => {
        return specialProductData.description.trim() !== '';
    };

    useEffect(() => {
        const allSelected =
            selectedFurnitureType &&
            selectedModel &&
            selectedColor &&
            selectedSize;
    
        const fetchPrice = async () => {
            if (!allSelected) {
                setPrice(null);
                onPriceChange({ ...product, price: 0 });
                return;
            }
    
            if (!dropdownHasChanged || isPriceManuallyEdited) return;
    
            setLoading(true);
            try {
                const result = await getProductPrice({
                    typeId: selectedFurnitureType,
                    modelId: selectedModel,
                    colorId: selectedColor,
                    sizeId: selectedSize,
                });
    
                const updatedProduct = {
                    ...product,
                    typeId: selectedFurnitureType,
                    modelId: selectedModel,
                    colorId: selectedColor,
                    sizeId: selectedSize,
                    price: result.price,
                    productPriceId: result.id,
                    quantity,
                    selectedVariants
                };
    
                setPrice(result.price);
                setBasePrice(result.price);
                onPriceChange(updatedProduct);
            } catch (error) {
                logError(error);
                console.error('Error fetching price:', error);
                setPrice(null);
                onPriceChange({ ...product, price: 0 });
            } finally {
                setLoading(false);
                setDropdownHasChanged(false);
            }
        };
    
        fetchPrice();

        if (onFormCompletion) {
            // Force special products to be considered complete if they have a description
            if (isSpecialProduct && product.isSpecial) {
                const isComplete = specialProductData.description.trim() !== '' && !!specialProductData.productTypeId;
                onFormCompletion(product.id, isComplete);
            } else {
                const isComplete = isFormComplete();
                onFormCompletion(product.id, isComplete);
            }
        }
    }, [
        selectedFurnitureType,
        selectedModel,
        selectedColor,
        selectedSize,
        dropdownHasChanged,
        isPriceManuallyEdited,
        isSpecialProduct,
        specialProductData.description,
    ]);

    const handleProductSelection = (selection) => {
        setSelectedFurnitureType(selection.typeId);
        setSelectedModel(selection.modelId);
        setSelectedColor(selection.colorId);
        setSelectedSize(selection.sizeId);
        
        if (selection.price !== null) {
            setPrice(selection.price);
            setBasePrice(selection.price);
            setManualPrice(selection.price);
            setIsPriceManuallyEdited(false);
            
            const updatedProduct = {
                ...product,
                typeId: selection.typeId,
                modelId: selection.modelId,
                colorId: selection.colorId,
                sizeId: selection.sizeId,
                price: selection.price,
                productPriceId: selection.productPriceId,
                quantity,
                selectedVariants
            };
            
            setDropdownHasChanged(true);
            onPriceChange(updatedProduct);
        }
    };
    
    const handleSpecialProductToggle = () => {
        const newIsSpecial = !isSpecialProduct;
        setIsSpecialProduct(newIsSpecial);
        
        const currentPrice = newIsSpecial ? 
            (specialProductData.price === '' ? 0 : Number(specialProductData.price)) : 
            product.price;
        
        const updatedProduct = {
            ...product,
            isSpecial: newIsSpecial,
            description: newIsSpecial ? specialProductData.description : '',
            price: newIsSpecial ? currentPrice : product.price,
            productTypeId: newIsSpecial ? (specialProductData.productTypeId || '') : undefined,
            quantity
        };
        
        onPriceChange(updatedProduct);
        
        if (onFormCompletion) {
            const isComplete = newIsSpecial
                ? specialProductData.description.trim() !== '' && !!specialProductData.productTypeId
                : isFormComplete();
            onFormCompletion(product.id, isComplete);
        }
    };
    
    const handleSpecialProductChange = (field, value) => {
        const newData = { ...specialProductData, [field]: value };
        setSpecialProductData(newData);
        
        let currentPrice;
        if (field === 'description') {
            currentPrice = product.price > 0 ? 
                product.price : 
                (specialProductData.price === '' ? 0 : Number(specialProductData.price));
        } else if (field === 'productTypeId') {
            currentPrice = product.price > 0 ?
                product.price :
                (specialProductData.price === '' ? 0 : Number(specialProductData.price));
        } else {
            currentPrice = value === '' ? 0 : Number(value);
        }
        
        const updatedProduct = {
            ...product,
            isSpecial: true,
            description: newData.description,
            price: currentPrice,
            productTypeId: newData.productTypeId ? Number(newData.productTypeId) : undefined,
            quantity
        };
        
        setTimeout(() => {
            onPriceChange(updatedProduct);
        }, 0);
        
        if (onFormCompletion) {
            const isComplete = newData.description.trim() !== '' && !!newData.productTypeId;
            onFormCompletion(product.id, isComplete);
        }
    };

    const handleQuantityChange = (e) => {
        const newQuantity = parseInt(e.target.value, 10);
        if (newQuantity > 0) {
            setQuantity(newQuantity);

            const updatedProduct = {
                ...product,
                quantity: newQuantity,
                isSpecial: isSpecialProduct,
                description: isSpecialProduct ? specialProductData.description : product.description,
                price: isSpecialProduct ? Number(specialProductData.price) : product.price
            };

            onPriceChange(updatedProduct);
        }
    };

    const handleVariantChange = (variantId, isSelected) => {
        const newSelectedVariants = isSelected 
            ? [...selectedVariants, variantId]
            : selectedVariants.filter(id => id !== variantId);
        
        setSelectedVariants(newSelectedVariants);
        
        // Recalcular precio con variantes
        const variantPrice = variants
            .filter(v => newSelectedVariants.includes(v.id))
            .reduce((sum, v) => sum + v.priceDelta, 0);
        
        const totalPrice = basePrice + variantPrice;
        setPrice(totalPrice);
        setManualPrice(totalPrice);
        
        const updatedProduct = {
            ...product,
            price: totalPrice,
            selectedVariants: newSelectedVariants
        };
        onPriceChange(updatedProduct);
    };

    const handleConfirmedPrice = (confirmedPrice) => {
        setPrice(confirmedPrice);
        const updatedProduct = {
          ...product,
          price: confirmedPrice,
          selectedVariants
        };
        onPriceChange(updatedProduct);
      };

    // Función para abrir el modal de agregar producto
    const handleShowAddProductModal = () => setShowAddProductModal(true);

    // Función para cerrar el modal
    const handleCloseAddProductModal = () => setShowAddProductModal(false);

    // Función para guardar el nuevo producto desde el modal
    const handleSaveProduct = async (newProductData) => {
        try {
            setSkipAutoSelection(true);

            const [models, colors, sizes] = await Promise.all([
                getFurnitureModelsByTypeId(newProductData.TypeId, false),
                getModelColors(newProductData.ModelId, false),
                getModelSizes(newProductData.ModelId, false),
            ]);

            setModels(models);
            setColors(colors);
            setSizes(sizes);

            setSelectedFurnitureType(newProductData.TypeId);
            setSelectedModel(newProductData.ModelId);
            setSelectedColor(newProductData.ColorId);
            setSelectedSize(newProductData.SizeId);
            setPrice(newProductData.Price);
            setBasePrice(newProductData.Price);
            setIsPriceManuallyEdited(false);
            setManualPrice(newProductData.Price);

            setTimeout(() => {
                const updatedProduct = {
                    ...product,
                    typeId: newProductData.TypeId,
                    modelId: newProductData.ModelId,
                    colorId: newProductData.ColorId,
                    sizeId: newProductData.SizeId,
                    price: newProductData.Price,
                };

                onPriceChange(updatedProduct);
                setShowAddProductModal(false);
                setSkipAutoSelection(false);
            }, 0);
        } catch (error) {
            logError(error);
            console.error('Error setting product from modal:', error);
        }
    };

    useEffect(() => {
    if (
        skipAutoSelection &&
        product.colorId &&
        colors.some(c => c.id === product.colorId)
    ) {
        setSelectedColor(product.colorId);
    }
    }, [colors, skipAutoSelection, product.colorId]);

    return (
        <div className="order-product-form">
            <Form>
                {/* Toggle para producto especial */}
                <Row className="mb-3">
                    <Col xs={12}>
                        <Form.Check
                            type="switch"
                            id={`special-product-${product.id}`}
                            label="Producto especial"
                            checked={isSpecialProduct}
                            onChange={handleSpecialProductToggle}
                        />
                    </Col>
                </Row>
                {!isSpecialProduct && (
                    /* Selector de productos normal */
                    <ProductSelector
                        initialTypeId={selectedFurnitureType}
                        initialModelId={selectedModel}
                        initialColorId={selectedColor}
                        initialSizeId={selectedSize}
                        onSelectionChange={handleProductSelection}
                        disabled={loading}
                        furnitureTypes={furnitureTypes}
                    />
                )}
                
                {isSpecialProduct && (
                    <>
                        <Row className="mb-3">
                            <Col xs={12}>
                                <Form.Label>Tipo de Producto</Form.Label>
                                <FavoriteDropdown
                                    value={specialProductData.productTypeId}
                                    onChange={(e) => handleSpecialProductChange('productTypeId', e.target.value)}
                                    options={furnitureTypes}
                                    placeholder="Seleccionar tipo"
                                    nameField="typeName"
                                    className="styled-select"
                                    data-testid="special-product-type-selector"
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col xs={12}>
                                <Form.Label>Descripción del producto</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={specialProductData.description}
                                    onChange={(e) => handleSpecialProductChange('description', e.target.value)}
                                    placeholder="Describe el producto especial..."
                                />
                            </Col>
                        </Row>
                    </>
                )}

                {/* Removed duplicate price field */}

                {/* Variantes */}
                {isFormComplete() && variants.length > 0 && (
                    <Row className="mb-3">
                        <Col xs={12}>
                            <Form.Label className="form-label">Variantes disponibles</Form.Label>
                            <div className="border p-3 rounded">
                                {variants.map((variant) => (
                                    <Form.Check
                                        key={variant.id}
                                        type="checkbox"
                                        id={`variant-${variant.id}`}
                                        label={`${variant.name} (+${variant.priceDelta}€)`}
                                        checked={selectedVariants.includes(variant.id)}
                                        onChange={(e) => handleVariantChange(variant.id, e.target.checked)}
                                        className="mb-2"
                                    />
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Precio unitario - Usado tanto para productos normales como especiales */}
                {((isFormComplete() && !isSpecialProduct) || isSpecialProduct) && (
                    <Row className="mb-3">
                        <Col xs={12}>
                        <Form.Label htmlFor="price" className="form-label">Precio unitario</Form.Label>
                        {!isSpecialProduct ? (
                            <PriceConfirmation
                                originalPrice={basePrice}
                                manualPrice={manualPrice}
                                setManualPrice={(val) => {
                                    setManualPrice(val);
                                    setIsPriceManuallyEdited(true);
                                }}
                                onConfirm={handleConfirmedPrice}
                                inputRef={priceInputRef}
                                className="styled-input"
                                disabled={loading}
                                message="Has modificado el precio manualmente más de un 5% respecto al recomendado. Escribe ENTENDIDO para confirmar."
                            />
                        ) : (
                            <Form.Control
                                type="number"
                                step="1"
                                value={specialProductData.price}
                                onChange={(e) => handleSpecialProductChange('price', e.target.value)}
                                className="styled-input"
                                placeholder="0"
                            />
                        )}
                        </Col>
                    </Row>
                )}
                {/* Removed duplicate special product price field */}

                {/* Campo de cantidad */}
                {((isFormComplete() && !isSpecialProduct) || isSpecialProduct) && (
                    <Row className="mb-3">
                        <Col xs={12}>
                            <Form.Label htmlFor="quantity" className="form-label">Cantidad</Form.Label>
                            <Form.Control
                                id="quantity"
                                type="number"
                                value={quantity}
                                onChange={handleQuantityChange}
                                className="styled-input"
                                min="1"
                            />
                        </Col>
                    </Row>
                )}

                {/* Agregar botón para abrir el modal */}
                {isPropietarioOrAdminOrGestion() && (
                    <Row className="mb-3">
                        <Col xs={12}>
                            <Button variant="secondary" onClick={handleShowAddProductModal}>
                                Crear Nuevo Producto
                            </Button>
                        </Col>
                    </Row>
                )}
            </Form>

            {/* Modal para crear nuevo producto */}
            <ProductEditorModal
                show={showAddProductModal}
                onHide={handleCloseAddProductModal}
                onSave={handleSaveProduct}
                mode="create"
            />
        </div>
    );
};

export default OrderProductsForm;
